import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Divider,
  Indicator,
  NumberFormatter,
  SimpleGrid
} from '@mantine/core';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import ResponsiveWebstoreModal from './ResponsiveWebstoreModal';
import { useCartController } from '../../../helpers/checkout/useCartController';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import { singularPluralFormat } from '../../../helpers/format';
import * as classes from '../../../styles/nestedStyles.module.css';
import AppImage from '../../common/AppImage';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import { useCartShippingController } from '../../../helpers/checkout/useCartShippingController';

const ChangeShippingModal = ({
  baseStoreUrl,
  mediaQueries,
  editModeEnabled,
  onNavigate,
  ecomStoreTheme,
  pathname
}) => {
  const { state: helperState, toggleWebstoreChangeShippingModal } = useContext(
    HelperContext
  );
  const { ecomStoreCart } = useCartController();
  const { onChangeShippingCosts } = useCartShippingController();
  const [formState, setFormState] = useState({ shippingOptions: [] });
  const { isTabletOrSmaller } = mediaQueries;

  const shippingCostsWithProducts =
    ecomStoreCart?.shippingCosts?.map((c) => ({
      ...c,
      products: [
        ...ecomStoreCart.ecomStoreCartProducts.filter((p) =>
          c.ecomProductIds.includes(p.pkEcomStoreCartProduct)
        )
      ]
    })) ?? [];

  useEffect(() => {
    if (helperState.webstore.changeShippingModal.isOpen) {
      // eslint-disable-next-line no-use-before-define
      onClose();
    }
  }, [pathname]);

  useEffect(() => {
    if (helperState.webstore.changeShippingModal.isOpen) {
      setFormState({
        shippingOptions: ecomStoreCart.checkoutCart.shippingCosts.map((s) => ({
          ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId,
          fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
          value: s.value,
          label: s.label
        }))
      });
    }
  }, [helperState.webstore.changeShippingModal.isOpen]);

  const onClose = () => {
    if (
      formState.shippingOptions.some(
        (s) =>
          s.value !==
          ecomStoreCart.checkoutCart.shippingCosts.find(
            (f) =>
              f.fkEcomExternalConnectionType ===
                s.fkEcomExternalConnectionType &&
              f.ecomExternalConnectionExternalId ===
                s.ecomExternalConnectionExternalId
          )?.value
      )
    ) {
      onChangeShippingCosts(ecomStoreCart.fkEcomStore, {
        shippingOptions: formState.shippingOptions
      });
    }

    toggleWebstoreChangeShippingModal({
      isOpen: false,
      ecomStoreProduct:
        helperState.webstore.changeShippingModal.ecomStoreProduct
    });
  };

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      editModeEnabled={editModeEnabled}
      formSectionProps={{
        onSubmit: () => {
          onClose();
        },
        submitTitle: 'Apply',
        submitColor: 'blue',
        isCancelHidden: true
      }}
      isOpen={helperState.webstore.changeShippingModal.isOpen}
      mediaQueries={mediaQueries}
      onClose={onClose}
      radius={0}
      size={600}
      title="Change shipping"
    >
      <AppStack style={{ gap: 20 }}>
        {shippingCostsWithProducts.map((s) => {
          const config = EXTERNAL_CONNECTION_CONFIGS.find(
            (f) =>
              f.pkEcomExternalConnectionType.toString() ===
              s.fkEcomExternalConnectionType.toString()
          );

          const selectedShippingCost = formState.shippingOptions.find(
            (f) =>
              f.ecomExternalConnectionExternalId ===
                s.ecomExternalConnectionExternalId &&
              f.fkEcomExternalConnectionType === s.fkEcomExternalConnectionType
          );

          return (
            <AppCard
              key={`${s.ecomExternalConnectionExternalId}-${s.fkEcomExternalConnectionType}`}
              radius={8}
              shadow="lg"
              style={{ padding: 0 }}
              withBorder
            >
              <AppStack style={{ gap: 0 }}>
                <AppFlexbox
                  style={{ padding: '16px 8px', backgroundColor: '#f3f3f3' }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Shipping for{' '}
                    {singularPluralFormat(
                      s.products.reduce((r, c) => r + c.count, 0),
                      'item',
                      'items'
                    )}
                  </AppText>
                </AppFlexbox>
                <Divider />
                {s.shippingOptions.map((option, index) => {
                  const configOption = config?.shippingOptions.find(
                    (f) => f.value === option.value
                  );
                  return (
                    <React.Fragment key={option.value}>
                      {index > 0 && <Divider />}
                      <AppFlexbox
                        className={classes['hover-action-card']}
                        onClick={() => {
                          setFormState({
                            ...formState,
                            shippingOptions: [
                              ...formState.shippingOptions.filter(
                                (f) =>
                                  f.ecomExternalConnectionExternalId !==
                                    s.ecomExternalConnectionExternalId ||
                                  f.fkEcomExternalConnectionType !==
                                    s.fkEcomExternalConnectionType
                              ),
                              {
                                ecomExternalConnectionExternalId:
                                  s.ecomExternalConnectionExternalId,
                                fkEcomExternalConnectionType:
                                  s.fkEcomExternalConnectionType,
                                value: option.label,
                                label: option.label
                              }
                            ]
                          });
                        }}
                        style={{
                          gap: 8,
                          padding: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                          <Checkbox
                            checked={
                              selectedShippingCost?.value === option.value ||
                              selectedShippingCost?.value === option.label
                            }
                            onChange={() => {}}
                          />
                          <AppStack style={{ gap: 0 }}>
                            <AppText style={{ fontSize: 14 }}>
                              {option.label}
                            </AppText>
                            {configOption?.description && (
                              <AppText
                                style={{
                                  fontSize: 12,
                                  color: '#666'
                                }}
                              >
                                {configOption.description}
                              </AppText>
                            )}
                          </AppStack>
                        </AppFlexbox>

                        <AppText style={{ fontSize: 14, fontWeight: 700 }}>
                          <NumberFormatter
                            decimalScale={2}
                            displayType="text"
                            fixedDecimalScale
                            prefix="$"
                            thousandSeparator
                            value={option.cost / 100}
                          />
                        </AppText>
                      </AppFlexbox>
                    </React.Fragment>
                  );
                })}
                <Divider />
                <AppFlexbox style={{ padding: '16px 8px' }}>
                  <SimpleGrid cols={3}>
                    {s.products.map((p) => (
                      <AppStack
                        key={p.pkEcomStoreCartProduct}
                        style={{ gap: 0 }}
                      >
                        <Indicator inline label={p.count} size={22}>
                          <AppStack
                            style={{
                              height: 100,
                              width: 100,
                              borderRadius: 8,
                              overflow: 'hidden',
                              border: '1px solid #E1E1E1'
                            }}
                          >
                            <AppImage
                              src={
                                p.ecomStoreProductVariant
                                  .ecomVendorProductVariant.previewImage ??
                                p.ecomStoreProductVariant.ecomStoreProduct
                                  .ecomVendorProduct.previewImage
                              }
                            />
                          </AppStack>
                        </Indicator>
                      </AppStack>
                    ))}
                  </SimpleGrid>
                </AppFlexbox>
              </AppStack>
            </AppCard>
          );
        })}
      </AppStack>
    </ResponsiveWebstoreModal>
  );
};

ChangeShippingModal.propTypes = {
  baseStoreUrl: PropTypes.string,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string
};

export default ChangeShippingModal;
